import { Box, HStack, VStack } from "@chakra-ui/react";
import Select from "components/Select";
import GreyBlackField from "components/DataDisplay/GreyBlackField";
import { FloatingField } from "design-system";
import { range } from "lodash";
import { Control, FieldErrors, UseFormSetValue } from "react-hook-form";
import { useMemo } from "react";

const COMBINED_REMINDER_OFFSET_EVENT_FIELD = {
  type: "SELECT",
  label: "When",
  placeholder: "When",
  options: [
    { label: "Before Start Date", value: { offsetDirection: "before", referenceEvent: "start_date" } },
    { label: "On Start Date", value: { offsetDirection: "exact", referenceEvent: "start_date" } },
    { label: "After Start Date", value: { offsetDirection: "after", referenceEvent: "start_date" } },
  ],
};

const OFFSET_VALUE_OPTIONS = range(1, 120).map((value) => ({ label: value.toString(), value }));

const OFFSET_UNIT_FIELD = {
  type: "SELECT",
  label: "Day/Week/Month",
  placeholder: "Day/Week/Month",
  name: "offsetUnit",
  options: [
    { label: "Day(s)", value: "day" },
    { label: "Week(s)", value: "week" },
    { label: "Month(s)", value: "month" },
    { label: "Monday(s)", value: "monday" },
    { label: "Tuesday(s)", value: "tuesday" },
    { label: "Wednesday(s)", value: "wednesday" },
    { label: "Thursday(s)", value: "thursday" },
    { label: "Friday(s)", value: "friday" },
    { label: "Saturday(s)", value: "saturday" },
    { label: "Sunday(s)", value: "sunday" },
  ],
};

export default function SchedulingParameters({
  label = "When",
  excludeBeforeStartDate = undefined,
  setValue,
  offset,
  referenceEvent,
  errors,
  control,
}: {
  label?: string;
  excludeBeforeStartDate?: true;
  offset: number;
  referenceEvent: string;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
  control: Control<any>;
}) {
  // Derive direction and value from signed offset for display
  const { offsetDirection, offsetValue } = useMemo(() => {
    if (offset === 0) {
      return { offsetDirection: "exact", offsetValue: 0 };
    } else if (offset > 0) {
      return { offsetDirection: "after", offsetValue: offset };
    }
    return { offsetDirection: "before", offsetValue: Math.abs(offset) };
  }, [offset]);

  const allowedOptions = COMBINED_REMINDER_OFFSET_EVENT_FIELD.options.filter(
    (option) => option.value.offsetDirection !== "before" || !excludeBeforeStartDate,
  );

  return (
    <VStack>
      <HStack width="100%" mb="1">
        <GreyBlackField label={label} width="100%">
          <Box w="100%">
            <Select
              isClearable={false}
              required
              options={allowedOptions as any}
              value={
                allowedOptions.find(
                  (option) =>
                    option.value.offsetDirection === offsetDirection && option.value.referenceEvent === referenceEvent,
                ) as any
              }
              onChange={(selected: any) => {
                if (selected) {
                  const newDirection = selected.value.offsetDirection;
                  setValue("referenceEvent", selected.value.referenceEvent, { shouldDirty: true });

                  // Calculate new offset based on direction change
                  if (newDirection === "exact") {
                    setValue("offset", 0, { shouldDirty: true });
                  } else if (newDirection === "before") {
                    setValue("offset", -Math.abs(offsetValue || 1), { shouldDirty: true });
                  } else {
                    setValue("offset", Math.abs(offsetValue || 1), { shouldDirty: true });
                  }
                }
              }}
              errors={errors}
              control={control}
            />
          </Box>
        </GreyBlackField>
      </HStack>

      {offsetDirection && offsetDirection !== "exact" && (
        <HStack width="100%">
          <Box display="inline-block" width="25%">
            <Select
              isClearable={false}
              required
              menuPlacement="top"
              options={OFFSET_VALUE_OPTIONS as any}
              value={OFFSET_VALUE_OPTIONS.find((opt) => opt.value === offsetValue) as any}
              onChange={(selected: any) => {
                if (selected) {
                  const newValue = selected.value;
                  const newOffset = offsetDirection === "before" ? -newValue : newValue;
                  setValue("offset", newOffset, { shouldDirty: true });
                }
              }}
            />
          </Box>
          <Box display="inline-block" width="40%">
            <FloatingField name="offsetUnit" errors={errors.offsetUnit?.message as string | undefined} required>
              <FloatingField.Label>Day/Week/Month</FloatingField.Label>
              <FloatingField.StaticSelect
                menuPlacement="top"
                isClearable={false}
                name="offsetUnit"
                options={OFFSET_UNIT_FIELD.options}
                control={control}
              />
            </FloatingField>
          </Box>
        </HStack>
      )}
    </VStack>
  );
}
